import { Stack } from '@fluentui/react';
import { Label, makeStyles, ProgressBar, SelectTabData, shorthands, Spinner, Tab, TabList, Text, tokens } from '@fluentui/react-components';
import { DataUsageRegular, Table24Regular, TableRegular } from '@fluentui/react-icons';
import { useFabricContext } from '@idmc-ms-fabric/integration';
import { NotificationToastDuration, NotificationType } from '@idmc-ms-fabric/integration';
import { JobStatus } from '@infa-service/mdp-orchestration-api';
import React, { lazy, ReactNode, Suspense, useEffect, useMemo, useState } from 'react';

import MessageScreen from '../../component/core/common-screen/MessageScreen';
import ProgressScreen from '../../component/core/progress/ProgressScreen';
import { FluentPanel, FluentPanelGroupVertical } from '../../component/core/reasiable-panel/FluentResizablePanel';
import StepProgress, { Steps, StepStatus } from '../../component/core/step-progress/StepProgress';
import { useIDQAPageContext } from '../../context/business/IDQAPage/IDQAPageContext';
import { useLoginContext } from '../../context/business/InfaLogin/InfaLoginContext';
import useDQTranslation from '../../hooks/useDQTranslation';
import i18nKeys from '../../i18n/i18nKeys';
import { createHierarchicalTree, devMode } from '../../utils/coreUtils';
import { getActiveJobStatus } from '../../utils/infa-utils';
import RunProfilingButton from './components/RunProfilingButton';
const ResultView = lazy(() => import('../../component/shared/result/ResultView'));

const useStyles = makeStyles({
	cntr: {
		height: '100%',
		...shorthands.margin(0, '10px', 0, 0),
	},
	body: {
		height: '100%',
	},
	header: {},
	tableCntr: {
		display: 'flex',
		...shorthands.padding('8px', 0, '8px', '12px'),
		alignItems: 'center',
		alignSelf: 'center',
		borderBottom: '1px solid #E0E0E0;',
	},
	tableNameLabel: {
		fontSize: '20px',
		marginLeft: '8px',
		fontWeight: tokens.fontWeightSemibold,
	},
	progressBar: {
		width: '200px',
	},
	icon: {
		width: '48px',
		height: '48px',
	},
});

enum TabId {
	results = 'results',
	rules = 'rules',
}
//TODO remove this hardcoded URL and use the one from IT service
const DEV_MODE_URL = 'https://mcc.rel1.infaqa.com';
function ProfilingDetailsView() {
	const { state, controller } = useIDQAPageContext();
	const { extensionClient } = useFabricContext();
	const {
		bootoutput: { serviceBaseURLs },
	} = useLoginContext();
	const { table, catalogSrc } = state;
	const { t } = useDQTranslation();
	const [tasksSteps, setTasksSteps] = useState<Steps[]>([]);
	const [urlToMccJobPage, setUrlToMcc] = useState<string>();
	const checkForJobComplete = useMemo(() => {
		if (!state.runningJobDetails) {
			return true;
		}
		return getActiveJobStatus().includes(state.runningJobDetails.status as JobStatus) ? false : true;
	}, [state.runningJobDetails?.status]);

	const style = useStyles();
	const [selectedTab, setSelectedTab] = useState<TabId>(TabId.results);
	const [profilingStatus, setProfilingStatus] = useState<'CHECKING' | 'AVAILABLE' | 'NOT_AVAILABLE'>('NOT_AVAILABLE');
	const isProfilingInProgress = controller.isProfilingRunningOnSelectedTable(state);
	const tabs: Array<{ id: TabId; label: string; content: () => ReactNode }> = useMemo(
		() => [
			{
				id: TabId.results,
				label: t(i18nKeys.main.results),
				content() {
					return (
						<Suspense fallback={<Spinner style={{ height: '100%' }} />}>
							<FluentPanel>
								<ResultView />
							</FluentPanel>
						</Suspense>
					);
				},
			},
		],
		[catalogSrc?.id, table?.hierarchy, t, checkForJobComplete],
	);
	const tableName = useMemo(() => {
		if (state.table) {
			return (
				<div className={style.tableCntr}>
					<TableRegular fontSize={20} />
					<Label className={style.tableNameLabel}>{state.table.name}</Label>
				</div>
			);
		}
		return null;
	}, [state.table]);
	const updateProfilingStatus = async () => {
		setSelectedTab(TabId.results);
		setProfilingStatus('CHECKING');
		try {
			const status = await controller.hasProfilingDetails(state);
			setProfilingStatus(status ? 'AVAILABLE' : 'NOT_AVAILABLE');
		} catch (e) {
			setProfilingStatus('NOT_AVAILABLE');
			extensionClient.notification.open({
				title: t(i18nKeys.main.errorFetchingProfilingStatus),
				notificationType: NotificationType.Error,
				duration: NotificationToastDuration.Short,
			});
		}
	};

	const updateTasksDetails = async () => {
		const { runningJobDetails } = state;
		if (runningJobDetails?.tasks && runningJobDetails.tasks.length > 0) {
			const taskDetails = createHierarchicalTree(runningJobDetails.tasks);
			const steps: Steps[] = taskDetails?.map((step) => ({
				id: step.id as string,
				message: step.name as string,
				state: step.status as unknown as StepStatus,
				childrens: step.childrens,
			}));
			if (steps && steps.length > 0) {
				setTasksSteps(steps);
			}
			const task = runningJobDetails.tasks.find((t) => t.jobId);
			if (serviceBaseURLs && task && task.jobId) {
				const url = devMode ? DEV_MODE_URL : serviceBaseURLs['mcp-app'];
				const newUrlToMccJobPage = `${url}/jobInfo/${task.jobId}`;
				if (!urlToMccJobPage || urlToMccJobPage !== newUrlToMccJobPage) {
					setUrlToMcc(newUrlToMccJobPage);
				}
			}
		} else {
			setTasksSteps([]);
			setUrlToMcc(undefined);
		}
	};
	useEffect(() => {
		if (state.table) {
			updateTasksDetails();
		}
	}, [state.table, state.runningJobDetails]);
	useEffect(() => {
		if (state.table) {
			updateProfilingStatus();
		}
	}, [state.table, checkForJobComplete]);

	return (
		<Stack className={style.cntr}>
			<Stack.Item className={style.header}>{tableName}</Stack.Item>
			<Stack.Item className={style.header}>
				<TabList
					selectedValue={selectedTab}
					onTabSelect={(_, data: SelectTabData) => {
						setSelectedTab(data.value as TabId);
					}}
				>
					{tabs.map(({ id, label }) => (
						<Tab key={id} id={id} value={id} disabled={profilingStatus !== 'AVAILABLE'}>
							{label}
						</Tab>
					))}
				</TabList>
			</Stack.Item>
			<Stack.Item className={style.body}>
				{state.table ? (
					profilingStatus === 'CHECKING' ? (
						<ProgressScreen icon={<Table24Regular className={style.icon} />} message={t(i18nKeys.main.fetchProfilingDetails, { name: state.table.name })} />
					) : profilingStatus === 'AVAILABLE' ? (
						<FluentPanelGroupVertical>{tabs.find((tab) => tab.id === selectedTab)?.content()}</FluentPanelGroupVertical>
					) : isProfilingInProgress ? (
						<StepProgress
							spinnerProps={{ appearance: 'primary', size: 'extra-tiny' }}
							steps={tasksSteps}
							title={t(i18nKeys.main.profilingProgress)}
							message={<Label disabled>{t(i18nKeys.main.hintRunProfilingPage)}</Label>}
							icon={<DataUsageRegular className={style.icon} />}
							url={urlToMccJobPage}
							urlText={t(i18nKeys.main.urlToMccJobPage)}
						/>
					) : (
						<Stack className={'stackPanel'} tokens={{ childrenGap: 10 }}>
							<Stack.Item>
								<DataUsageRegular className={style.icon} />
							</Stack.Item>
							<Stack.Item>
								<ProgressBar className={style.progressBar} value={isProfilingInProgress ? undefined : 1} thickness="medium" />
							</Stack.Item>
							<Stack.Item>
								<Text size={500} weight="semibold">
									{isProfilingInProgress ? t(i18nKeys.main.profilingProgress) : t(i18nKeys.main.runProfilingForTable, { asset: state.table?.name })}
								</Text>
							</Stack.Item>
							<Stack.Item>
								<Label disabled>{t(i18nKeys.main.hintRunProfilingPage)}</Label>
							</Stack.Item>
							<Stack.Item>
								<RunProfilingButton />
							</Stack.Item>
						</Stack>
					)
				) : (
					<MessageScreen icon={<Table24Regular className={style.icon} />} message={t(i18nKeys.main.selectTableToProceed)} />
				)}
			</Stack.Item>
		</Stack>
	);
}

export default ProfilingDetailsView;
